import { useParams, Link } from 'react-router-dom';
import { getCategoryBySlug, categories } from '@/mocks/navigation';
import { getArticlesByCategory } from '@/mocks/articles';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import NotFound from '@/pages/NotFound';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : null;

  if (!category) {
    return <NotFound />;
  }

  const categoryArticles = getArticlesByCategory(category.slug);

  return (
    <>
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${category.name} - 蓝色导航发布页`,
          description: category.description,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          items: [
            { name: '首页', url: '/' },
            { name: category.name, url: `/category/${category.slug}` },
          ],
        }}
      />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center bg-secondary-100 rounded-lg">
                <i className={`${category.icon} text-2xl text-secondary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    cat.slug === category.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 border border-background-200/70 text-foreground-600 hover:border-primary-200 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop>
              {category.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                      <i className="ri-external-link-line text-secondary-600 group-hover:text-primary-600 transition-colors"></i>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {link.name}
                      </h3>
                      <span className="text-xs text-foreground-400 block truncate mt-0.5">{link.url}</span>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                    {link.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {categoryArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-8">
                {category.name}相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.publishDate}</span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime}</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mt-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}